/**
 * @ngdoc directive
 * @name mx.components:mxGrid
 * @module mx.components
 * @restrict E
 *
 * @description
 * Visualizes table/grid data.
 *
 * The following example shows how to use mxGrid.
 * The mxGrid expects an attribute 'data' which is an array.
 *
 * ```html
 * <mx-grid data="vm.myData"></mx-grid>
 * ```
 * where: vm.myData = [{'title': 'My title', 'description': 'Some text'}, ... ]
 *
 * Also data can be passed through an 'options' attribute like this:
 * ```html
 * <mx-grid options="vm.gridOptions"></mx-grid>
 * ```
 * where: vm.gridOptions = { data: [{'title': 'My title', 'description': 'Some text'}, ... ] }
 *
 * NOTE: To show 'Loading' indicator, data must be <null> or <undefined> (meaning that data not received yet).
 * Empty array means that data is loaded but it contains no entries.
 *
 * The 'selection-changed' attribute can be used if needed.
 * It expects a function, which will be executed when some item(s) a (un)selected.
 * This function receives an array of selected items as a parameter.
 * This event can be passed in 'options' attribute like a 'selectionChanged' key, similar with 'data' attribute.
 *
 * The 'on-item-click' attribute can be used if it's need to set row click handler.
 * Handler-function gets corresponding item-object as parameret.
 *
 * The 'on-item-dbl-click' attribute can be used if it's need to set row double click handler.
 * Handler-function gets corresponding item-object as parameret.
 *
 * The 'on-right-click' allows to set handler for right click.
 *
 * 'columns' attribute expects an array of columns with mandatory keys: 'Name', Title',
 * and not mandatory:
 *        - 'IsDisplay'    - (boolean) - show or hide column
 *        - 'Width'        - (integer) width in pixels. 0 - if 'autosize'
 *        - 'Sorting'      - 0 - don't sort, 1 - sort ASC, 2 - sort DESC, 4 - disabled
 *        - 'Alignment'    - 0 - left, 1 - center, 2 - right
 *
 *
 *
 *  ### Stylebehavior (html/css)
 *
 *  The Grid itself has by default a minimum height of 250px and sets its width automatically to the parent container.
 *  The size and general style behaviour can be changed by adding styles/classes to the mx-grid directive itself.
 *
 *  - Example for auto-height (grid sets its height to the parent-containers 500px):
 *
 * ```html
 * <div name="parent-container" style="height:500px;" layout="column">
 *  <mx-grid
 *    flex
 *    data="vm.myData"
 *    options="vm.gridOptions"
 *    selection-changed="vm.selectionChanged(selectedItems)"
 *    selected-items="vm.selectedItems"
 *    data-get-item-actions="vm.getItemActions(item)">
 *  </mx-grid>
 * </div>
 * ```
 *
 *  - Example for custom height:
 *
 * ```html
 * <div name="parent-container" style="height:500px;">
 *  <mx-grid
 *    style="height: 350px"
 *    data="vm.myData"
 *    options="vm.gridOptions"
 *    selection-changed="vm.selectionChanged(selectedItems)"
 *    selected-items="vm.selectedItems"
 *    data-get-item-actions="vm.getItemActions(item)">
 *  </mx-grid>
 * </div>
 * ```
 *
 * It's possible to disable a Sorting on grid by setting `gridOptions.enableSorting = false`.
 *
 * Attribute `on-right-click` allows to set handler for right click on table rows.
 * Expect function that returns a promise, success method of which give us an array of items for `mx-dropdown-menu`.
 *
 * Attribute `on-header-right-click` allows to set handler for right click on table headers.
 * Expect function that returns a promise, success method of which give us an array of items for `mx-dropdown-menu`.
 *
 *
 *
 *  - Usage:
 *
 * ```html
 *  <mx-grid
 *    data="vm.myData"
 *    options="vm.gridOptions"
 *    >
 *  </mx-grid>
 * ```
 *
 **/

MxGridCtrl.$inject = [
	'$scope',
	'uiGridConstants',
	'$templateCache',
	'$timeout',
	'$element',
	'gridUtil',
	'mx.internationalization',
	'mx.shell.Config',
	'mx.app.DeviceService',
	'$mdConstant',
	'mx.components.MxGridHelper',
	'$mdUtil',
];

function MxGridCtrl($scope, uiGridConstants, $templateCache, $timeout, $element, gritUtil, i18n, mxConfig, deviceService, $mdConstant, gridHelper, $mdUtil) {
	var vm = this;
	var loadOptions, useExternalDataLoading, useExternalSorting;
	var previousClickedRowIndex = -1;
	var templates = loadTemplates();

	vm.rows = [];
	vm.gridOptions = {
		// oprimization ...
		virtualizationThreshold: 50,
		excessRows: 20,
		columnVirtualizationThreshold: 3,
		excessColumns: 3,
		selectionRowHeaderWidth: 30,
		headerRowHeight: 46,
		minRowsToShow: deviceService.screenMobile() ? 5 : 10,
		enablePaginationControls: false,
		enableExpandable: true,
		enableExpandableRowHeader: false,
		enableColumnMenus: false,
		disableExport: true,
		flatEntityAccess: true,
	};

	vm.isStaticMode = true;
	vm.disableExport = false;

	vm.$onInit = function () {
		if (vm.mxContainerCtrl) {
			vm.mxContainerCtrl.registerChild($scope);
		}

		//Overrides angular-ui-grids on-mousewheel function, which destroys scrolling when the grid has a certain height
		gritUtil.on.mousewheel = function () {};

		vm.options = vm.options || {};
		vm.imgHost = mxConfig.get('imgHosts').attachments;
		vm.currencyConfig = mxConfig.get('currency');

		loadOptions = {};

		vm.loadData = (vm.options && vm.options.loadData) || vm.loadData;

		$scope.highlightOnClick = vm.options && vm.options.highlightOnClick ? vm.options.highlightOnClick : true;

		useExternalDataLoading = !!vm.loadData;
		useExternalSorting = useExternalDataLoading && vm.options.useExternalSorting;

		angular.extend(vm.gridOptions, {
			enableHorizontalScrollbar: vm.options && vm.options.enableHorizontalScrollbar !== undefined ? vm.options.enableHorizontalScrollbar : 1,
			rowHeight: vm.options.rowHeight || 30,
			enableCellEdit: false,
			enableSorting: vm.options && typeof vm.options.enableSorting === 'boolean' ? vm.options.enableSorting : true,
			parameters: vm.options && vm.options.parameters,
			rowEquality: vm.options && vm.options.rowIdentity && vm.options.rowIdentity.equal,
			gridMenuCustomItems: vm.options && vm.options.gridMenuCustomItems,
			hideHeader: true,
			useExternalFiltering: vm.options.enableFiltering,
			enableFiltering: vm.options.enableFiltering,
			hideExport: !!vm.options.hideExport,
		});

		$scope.title = vm.title;

		const debouncedClickHandler = $mdUtil.debounce((type, row) => {
			switch (type) {
				case 'click':
					vm.currentItem = row.entity;
					vm.options.onItemClick?.(row.entity);
					break;
				case 'dblclick':
					vm.options.onRowDblClick?.(row.entity);
					break;
			}
		}, 250);

		const clickHandler = ($event) => {
			$event.preventDefault();
			$event.stopPropagation();
			const row = getClickedRow($event.target);
			if (!row) {
				return;
			}
			const isOnCheckboxClick = $($event.target).closest('.mx-grid-checkbox').length > 0;
			if (isOnCheckboxClick) {
				onCheckboxClick($event, row);
			} else {
				debouncedClickHandler($event.type, row);
			}
		};

		$scope.onLeftClick = ($event) => clickHandler($event);
		$scope.onDblClick = ($event) => clickHandler($event);

		$scope.onRightClick = function ($event) {
			$event.preventDefault();
			$event.stopPropagation();

			if (vm.options.onRightClick) {
				var row = getClickedRow($event.target);

				$timeout(function () {
					vm.options.onRightClick(row.entity).then(function (menuItems) {
						vm.menuItems = menuItems;
						if (menuItems.length) {
							vm.dropdownEvent = $event;
							vm.isDropdownOpen = true;
						}
					});
				}, 100);
			}
		};

		angular.extend(vm.gridOptions, {
			useExternalPagination: useExternalDataLoading,
			useExternalSorting: useExternalSorting,
		});

		vm.loading = true;

		if (!Array.isArray(vm.selectedItems)) {
			vm.selectedItems = [];
		}

		vm.gridOptions.onRegisterApi = function (gridApi) {
			vm.gridApi = gridApi;

			if (vm.options && vm.options.onRegisterApi) {
				vm.options.onRegisterApi(gridApi);
			}

			gridApi.core.on.sortChanged($scope, function (grid, sortColumns) {
				vm.onSortChanged({ columns: sortColumns });
				loadOptions.sortColumns = sortColumns;
				reloadData();
			});

			gridApi.core.on.columnVisibilityChanged($scope, function (column) {
				vm.onColumnVisibilityChanged({ column: column });
			});

			gridApi.colResizable.on.columnSizeChanged($scope, function (column, columnWidthShift) {
				var absoluteWidth;
				gridApi.grid.columns.forEach(function (col) {
					if (col.name === column.name) {
						absoluteWidth = col.drawnWidth;
					}
				});
				vm.onColumnSizeChanged({
					column: column,
					columnWidthShift: columnWidthShift,
					absoluteWidth: absoluteWidth,
				});
			});

			gridApi.colMovable.on.columnPositionChanged($scope, function (column, oldPosition, newPosition) {
				vm.onColumnPositionChanged({
					column: column,
					oldPosition: oldPosition,
					newPosition: newPosition,
					columns: gridApi.grid.columns,
				});
			});

			vm.options.refreshGridCanvas = function () {
				gridApi.grid.redrawInPlace(true);
				$scope.$evalAsync(function () {
					gridApi.grid.refreshCanvas(true);
					gridApi.grid.callDataChangeCallbacks(uiGridConstants.dataChange.ROW);
				});
			};

			// this functional can be removed if this PR is approved (or similar) https://github.com/angular-ui/ui-grid/pull/6940
			preventHiddenGridRefresh();

			vm.gridApi.grid.registerStyleComputation({
				priority: 20,
				func: function () {
					return vm.gridApi.grid.columns.reduce(function (accumulator, column) {
						var columnStyles = '';
						if (!column.colDef.visible) {
							columnStyles = ' .grid' + vm.gridApi.grid.id + ' ' + column.getColClass(true) + ' { display:none }';
						}
						return accumulator + columnStyles;
					}, '');
				},
			});

			gridApi.core.on.renderingComplete($scope, () => {
				gridHelper.preventStuckingMovingColumns($scope, $element);
			});

			gridApi.core.on.rowsRendered($scope, function () {
				vm.staticCanvas = vm.staticCanvas || $element.find('.mx-grid-static-canvas')[0];
				refreshStaticGrid();

				$scope.$applyAsync(function () {
					highlightCurrentRow();
				});

				updateSelection(vm.selectedItems);

				vm.rowsRendering = false;

				vm.gridViewport = vm.gridViewport || $element.find('.ui-grid-viewport');
				vm.gridViewport.scrollTop(0);

				setTimeout(() => {
					// ARIA: avoid reading grouping icon name ("filter_none") by screenreader (JAWS)
					$element.find('.mx-grid-column--grouping-icon').removeAttr('tabindex');
				});
			});
		};

		vm.selectAllItems = selectAllItems;
	};

	function notifySelectionChanged() {
		vm.onSelectionChanged({
			selectedItems: vm.selectedItems
				.filter(function (entity) {
					return entity.__isSelected;
				})
				.concat(
					vm.rows
						.filter(function (row) {
							return row.selected;
						})
						.map(function (row) {
							return row.entity;
						})
						.filter(function (entity) {
							return vm.selectedItems.indexOf(entity) === -1;
						})
				),
		});
	}

	function selectAllItems() {
		vm.allSelected = !vm.allSelected;
		vm.rows.forEach(function (row) {
			row.selected = vm.allSelected;
			row.entity.__isSelected = vm.allSelected;
		});
		notifySelectionChanged();

		vm.onSelectAllClick({ allSelectedState: vm.allSelected });

		previousClickedRowIndex = -1;
	}

	function onCheckboxClick(ev, clickedRow) {
		ev.preventDefault();
		ev.stopPropagation();

		var selected = !clickedRow.selected;

		if (ev.shiftKey && previousClickedRowIndex !== -1) {
			var startIndex = Math.min(clickedRow.index, previousClickedRowIndex);
			var endIndex = Math.max(clickedRow.index, previousClickedRowIndex);

			vm.rows.forEach(function (row, index) {
				if (index < startIndex || index > endIndex) {
					return;
				}
				row.selected = selected;
				row.entity.__isSelected = selected;
			});
		} else {
			clickedRow.selected = selected;
			clickedRow.entity.__isSelected = selected;
		}

		previousClickedRowIndex = clickedRow.index;
		vm.allSelected = isAllItemsSelected();
		notifySelectionChanged();
	}

	function isAllItemsSelected() {
		return (
			vm.rows &&
			vm.rows.length &&
			vm.rows.every(function (row) {
				return row.selected;
			})
		);
	}

	function preventHiddenGridRefresh() {
		var allResizeEvents = $._data(window, 'events').resize || [];
		var last = allResizeEvents.length - 1;
		var uiGridResizeHandler = allResizeEvents[last].handler;
		if (uiGridResizeHandler.toString().indexOf('.gridWidth') > 0) {
			allResizeEvents[last].handler = function _mxGridResize() {
				if ($element.is(':visible')) {
					uiGridResizeHandler();
				}
			};

			$element.on('$destroy', function () {
				$(window).off('resize', uiGridResizeHandler);
			});
		}
	}

	$scope.$watch('vm.options.enableSorting', function () {
		if (typeof vm.options.enableSorting === 'boolean' && vm.gridOptions.columnDefs) {
			vm.gridOptions.enableSorting = vm.options.enableSorting;
			vm.gridOptions.columnDefs = vm.gridOptions.columnDefs.map(function (col) {
				return $.extend({}, col, { enableSorting: vm.options.enableSorting });
			});
		}
	});

	$scope.$watch('vm.loading', function (newValue, oldValue) {
		var loadingFinished = oldValue && !newValue;
		if (loadingFinished) {
			vm.rowsRendering = true;
			vm._finishRenderingTimeout = $timeout(function () {
				vm.rowsRendering = false;
			}, 500);
		}
	});

	$scope.$watchGroup(['vm.data', 'vm.options.columns'], function (newValues) {
		var data = newValues[0],
			columns = newValues[1],
			updateColumns = Array.isArray(columns) && columns !== vm._columns;

		if (vm._finishRenderingTimeout) {
			$timeout.cancel(vm._finishRenderingTimeout);
			vm._finishRenderingTimeout = null;
		}

		vm.rowsRendering = false;

		if (updateColumns) {
			vm.rowsRendering = true;
			vm._columns = columns;
			setColumns(columns);
		}
		if (Array.isArray(data) && data !== vm._data) {
			vm.rowsRendering = true;
			vm._data = data;
			if (vm.gridApi && !updateColumns) {
				// updating ui-grid columns fires rowsRendered event so check it to avoid double re-render
				vm.gridApi.grid.modifyRows([]); // We use custom render instead of ui-grid render, but modifyRows is used to fire events. To avoid unnecessary computations of ui-grid render it is always set empty array
			}

			if (data.length) {
				vm.gridOptions.disableExport = false;
			}
		}
	});

	window.onbeforeprint = function () {
		$scope.$evalAsync(function () {
			vm.gridOptions.minRowsToShow = vm.data.length;
			vm.gridOptions.virtualizationThreshold = vm.gridOptions.minRowsToShow;
			$element.find('.grid').css({ height: 60 + vm.gridOptions.minRowsToShow * vm.gridOptions.rowHeight + 'px' });
			$element.find('.ui-grid-viewport').css({
				height: 60 + vm.gridOptions.minRowsToShow * vm.gridOptions.rowHeight + 'px',
				overflow: 'visible',
			});
			$element.find('.ui-grid').css('position', 'static');
		});
	};
	window.onafterprint = function () {
		$scope.$evalAsync(function () {
			vm.gridOptions.minRowsToShow = deviceService.screenMobile() ? 5 : 10;
			vm.gridOptions.virtualizationThreshold = vm.gridOptions.minRowsToShow;
			$element.find('.ui-grid-viewport').css({
				height: '',
				overflow: 'scroll',
			});
			$element.find('.ui-grid').css('position', 'absolute');
		});
	};

	$scope.$watchCollection('vm.selectedItems', function (newList) {
		updateSelection(newList);
	});

	$scope.$watch('vm.options.rowHeight', function (rowHeight) {
		vm.gridOptions.rowHeight = rowHeight || 30;
	});

	$scope.$watch('vm.currentItem', function (newCurrentItem, oldCurrentItem) {
		if (newCurrentItem !== oldCurrentItem) {
			$timeout(function () {
				highlightCurrentRow();
			});
		}
	});

	// Trick which fixes sets of width/height to 0 for mx-grids which are hidden on UI
	$scope.$watch('vm.gridApi.grid.gridWidth', function (newVal, oldVal) {
		if (newVal === 0 && oldVal !== 0 && typeof oldVal === 'number') {
			vm.gridApi.grid.gridWidth = oldVal;
		}
	});
	$scope.$watch('vm.gridApi.grid.gridHeight', function (newVal, oldVal) {
		if (newVal === 0 && oldVal !== 0 && typeof oldVal === 'number') {
			vm.gridApi.grid.gridHeight = oldVal;
		}
	});

	vm.onColumnFilterMenuKeyDown = onColumnFilterMenuKeyDown;

	return vm;

	function onColumnFilterMenuKeyDown($event) {
		// By default md-menu will be closed if TAB is pressed.
		// Here this TAB-pressed event is stopped in all cases except pressed on ApplyButton.
		// ApplyButton is the last element of filter menu, so after TAB is pressed on it
		// the menu should be closed and keyboard navigation will continue throw other part of the document
		if ($event.which === $mdConstant.KEY_CODE.TAB) {
			if (!$event.target.classList.contains('mx-grid--column-filter-panel-apply')) {
				$event.stopPropagation();
			}
		}

		// Left/Right keys doesn't work for <input/> inside <md-menu-content/>. Let's prevent it
		if ($event.which === $mdConstant.KEY_CODE.LEFT_ARROW || $event.which === $mdConstant.KEY_CODE.RIGHT_ARROW) {
			$event.stopPropagation();
		}
	}

	function updateSelection(newList) {
		if (!Array.isArray(newList) || !vm.rows) {
			return;
		}
		vm.rows.forEach(function (row) {
			row.selected = false;
		});

		newList.forEach(function (newItem) {
			vm.rows.forEach(function (row) {
				if (vm.options.rowIdentity.equal(newItem, row.entity)) {
					row.selected = true;
					row.entity.__isSelected = true;
				}
			});
		});

		vm.allSelected = isAllItemsSelected();
	}

	function processObject(item, col) {
		var data = item[col.field];
		var hasData = typeof data !== 'undefined' && data !== null;
		if (!col || (!hasData && col.colDef.type !== 'customFormat')) {
			return '';
		}
		var column = col.colDef.item;

		if (!column || column.IsEnum) {
			return data;
		}

		switch (column.AttributeValueType) {
			case mx.shell.ObjectPropertyType.String.name:
			case mx.shell.ObjectPropertyType.Guid.name:
			case mx.shell.ObjectPropertyType.Text.name:
				return typeof data === 'string' ? mx.components.Utils.htmlEncode(data) : data;
			case mx.shell.ObjectPropertyType.Integer.name:
			case mx.shell.ObjectPropertyType.Float.name:
			case mx.shell.ObjectPropertyType.Long.name:
			case mx.shell.ObjectPropertyType.Decimal.name:
				return col.colDef.item.AttributeValueType && !col.colDef.item.ReferenceTo
					? i18n.formatNumber(data, col.colDef.item.AttributeValueType, col.colDef.item.Format)
					: data;
			case mx.shell.ObjectPropertyType.Currency.name:
				var currencyConfig = vm.currencyConfig;
				return typeof data === 'object'
					? i18n.formatCurrency(
							data,
							undefined,
							currencyConfig && currencyConfig.showCurrencyCode !== true && currencyConfig.showCurrencyCode !== 'true',
							currencyConfig.availableCurrencies
					  )
					: data;
			case mx.shell.ObjectPropertyType.Date.name:
				return typeof data === 'string'
					? '<span class="mx-grid-cell-date" aria-hidden="true">' +
							i18n.formatUTCDate(data, col.colDef.item.Format) +
							'</span>' +
							'<span class="mx-aria-only">' +
							i18n.formatUTCDateForAria(data, col.colDef.item.Format) +
							'</span>'
					: data;
			case mx.shell.ObjectPropertyType.Color.name:
				return typeof data === 'string' ? data.replace(/0xff/i, '#') : data;
			default:
				return data;
		}
	}

	function reloadData() {
		if (vm.loadData) {
			vm.loadData(loadOptions);
		}
	}

	function getRowsDomElements() {
		return Array.prototype.slice.call(vm.staticCanvas.children);
	}

	function highlightRow(row) {
		if (!row) {
			return;
		}
		var rowEls = getRowsDomElements();
		rowEls.forEach(function (rowEl) {
			var highlightedRow = parseInt(rowEl.getAttribute('data-row-index'), 10) === row.index;

			if (!highlightedRow && rowEl.classList.contains('grid-row_active')) {
				rowEl.classList.remove('grid-row_active');
			}

			if (highlightedRow && !rowEl.classList.contains('grid-row_active')) {
				rowEl.classList.add('grid-row_active');
			}
		});
	}

	function getCurrentRow() {
		return vm.rows.find(function (row) {
			return vm.options.rowIdentity.equal(vm.currentItem, row.entity);
		});
	}

	function highlightCurrentRow() {
		highlightRow(getCurrentRow());
	}

	function handleScript(column, item, script) {
		var cacheFieldName = '_' + item.Name + '_scriptResult';
		if (item[cacheFieldName]) {
			return item[cacheFieldName];
		} else {
			try {
				var func = new Function('$item', '$value', script);
				return func(item, item[column.Name + '_Value'] || item[column.Name]);
			} catch (e) {
				var type = column.ImageScript ? ' [Image Script]' : column.ColorScript ? ' [Color Script]' : '';
				console.error('Error in DataQuery column ' + (column.Title || column.Name) + type + ': ' + e.message);
				return null;
			}
		}
	}

	function graphicType(item, col) {
		var path = getGraphicPath(item, col);
		if (!path) {
			return 0;
		}
		if (path.indexOf('/') >= 0) {
			return 1;
		}
		return 2;
	}

	function calcColor(item, column) {
		var clr = null;

		if (column.ColorType) {
			clr = handleScript(column, item, column.ColorScript);
		} else {
			clr = item[column.Name + '_Color'];
		}

		return clr && typeof clr === 'string' ? clr.trim().toLowerCase().replace('0xff', '#') : null;
	}

	function getGraphicPath(item, col) {
		var column = col.colDef.item;

		var imageString;
		if (column.ImageScript) {
			imageString = handleScript(column, item, column.ImageScript);
		} else {
			imageString = item[column.Name + '_Image'] || item[column.Name];
		}

		return !imageString || imageString.indexOf('/') >= 0 ? imageString : imageString.trim().replace(/\s+/g, '_');
	}

	function colorToRgba(color, alpha) {
		if (color && color !== null && color.includes('#')) {
			// if (hex) -> rgba
			var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
			if (result) {
				return 'rgba(' + parseInt(result[1], 16) + ',' + parseInt(result[2], 16) + ',' + parseInt(result[3], 16) + ',' + (alpha || '0.5') + ')';
			}
		}

		return color;
	}

	function setColumns(columns) {
		var rowTextData = _.find(columns, ['ColorType', 3]);
		var rowBgData = _.find(columns, ['ColorType', 4]);

		// Sorts columns on the client side (e.g. mx-grid with custom columns)
		if (columns.length > 0 && typeof columns[0].position !== 'undefined') {
			columns = columns.sort(function (a, b) {
				return a.position - b.position;
			});
		}

		columns = columns.filter(function (clm) {
			return clm.DisplayType !== 2; // Exclude Visible None
		});

		vm.gridOptions.columnDefs = (
			vm.enableRowSelection !== 'false' && vm.options.enableRowSelection !== false
				? [
						{
							Type: 'selectItemCheckbox',
							AttributeValueType: 'selectItemCheckbox',
							Alignment: '1',
							Name: 'Sys-selectItemCheckbox',
							Width: '40px',
							IsDisplay: true,
							visible: true,
							AriaHidden: true,
						},
				  ]
				: []
		)
			.concat(columns)
			.map(function (column) {
				var alignClass = 'mx-grid-cell-' + ((column.Alignment === 0 && 'left') || (column.Alignment === 1 && 'center') || (column.Alignment === 2 && 'right'));
				var headerTemplate = column.headerCellTemplate || templates.headerTemplate;

				switch (column.AttributeValueType) {
					case 'selectItemCheckbox':
						headerTemplate = templates.selectItemCheckboxHeader();
						break;
					case 'quickfilter':
						headerTemplate = templates.quickFilterHeader(column.Labels, column.Parts);
						break;
				}

				var width = parseInt(column.width || column.Width, 10) || undefined;
				var field = column.field || column.path || (column.Name && column.Name.replace(/\s+/g, '')) || null;
				var visible = column.IsDisplay && (column.visible === undefined ? column.DisplayType !== 1 : column.visible);

				var mappedColumn = {
					field: field,
					name: field,
					type: 'string',
					item: column,
					rowTextData: rowTextData,
					rowBackgroundData: rowBgData,

					displayName: column.Title || column.title || column.displayName || column.DisplayName || '',
					visible: visible,
					enableColumnMenu: false,
					width: width,
					minWidth: 50 || Math.max(column.minWidth, 50),
					formatFn: column.formatFn,
					enableCellEdit: false,
					cellClass: alignClass,
					headerCellClass: alignClass,
					enableSorting: column.Sorting !== 4,
					headerCellTemplate: headerTemplate,
					cellFilter: column.cellFilter,
					filterHeaderTemplate: templates.columnFilter,
					filter: column.filter,
					groupingIsAvailable: column.GroupingIsAvailable,
					ariaHidden: column.AriaHidden,
					onGroupingClick: function (event, colDef) {
						vm.onGroupingClick({ event: event, column: colDef.item });
					},
				};

				if (column.Sorting === 1 || column.Sorting === 2) {
					mappedColumn.sort = {
						direction: (column.Sorting === 1 && uiGridConstants.ASC) || (column.Sorting === 2 && uiGridConstants.DESC),
						priority: column.SortingPriority,
					};
				}

				Object.defineProperty(column, 'visible', {
					configurable: true,
					get: function () {
						return visible;
					},
					set: function (newValue) {
						if (newValue !== visible) {
							mappedColumn.visible = newValue && column.IsDisplay;
							vm.gridApi.core.notifyDataChange(uiGridConstants.dataChange.COLUMN);
							visible = newValue;
						}
					},
				});

				return mappedColumn;
			});
	}

	function getColorStyle(item, col, isText) {
		var colorType = isText ? 1 : 2;
		var columnWithRowColor = isText ? col.colDef.rowTextData : col.colDef.rowBackgroundData;
		var color = null;

		if (!isText && col.colDef.item.Color) {
			color = col.colDef.item.Color;
		}

		if (!color && col.colDef.item.ColorType === colorType) {
			color = calcColor(item, col.colDef.item);
		}

		if (!color && columnWithRowColor) {
			color = calcColor(item, columnWithRowColor);
		}

		if (color) {
			if (color.indexOf(':') > 0) {
				var arr = color.split(':');
				return arr[0] + ':' + arr[1] + ';';
			} else {
				return isText ? 'color' + ':' + color + '; ' : 'background-color' + ':' + colorToRgba(color, 0.25) + '; ';
			}
		}

		return '';
	}

	function getCellContentTemplate(item, column, itemIndex) {
		var colDefItem = column.colDef.item;
		var defaultCellTemplate =
			'<div class="ui-grid-cell-contents" ' + 'style="' + getColorStyle(item, column, true) + '"' + '>' + (processObject(item, column) || '&nbsp;') + '</div>';

		if (colDefItem.DisplayImage > 0) {
			var type = graphicType(item, column);
			var imageCell = '';
			switch (type) {
				case 1:
					imageCell = '<img src="' + vm.imgHost + getGraphicPath(item, column) + '" title="' + item[column.field] + '">';
					break;
				case 2:
					imageCell =
						'<div title="' +
						item[column.field] +
						'">' +
						'<md-icon class="mx-grid-icon" style="' +
						getColorStyle(item, column, true) +
						'">' +
						getGraphicPath(item, column) +
						'</md-icon>' +
						'</div>';
			}
			if (colDefItem.DisplayImage === 1) {
				return imageCell;
			} else {
				return imageCell + defaultCellTemplate;
			}
		}

		switch (colDefItem.AttributeValueType) {
			case mx.shell.ObjectPropertyType.Icon.name:
				return (
					'<div title="' +
					item[column.field] +
					'" class="mx-grid-icon-picker-static">' +
					'<md-icon class="mx-grid-icon" >' +
					item[column.field] +
					'</md-icon>' +
					'<span>' +
					item[column.field] +
					'</span>' +
					'</div>'
				);
			case mx.shell.ObjectPropertyType.Color.name:
				return (
					'<div class="mx-grid-color-picker-static">' +
					'<div class="color-box" ' +
					(item[column.field] ? 'style="background-color=' + processObject(item, column) + '"' : '') +
					'>&nbsp;</div>' +
					'<div class="color-value">' +
					processObject(item, column) +
					'</div>'
				);
			case mx.shell.ObjectPropertyType.Boolean.name:
				return (
					'<md-icon class="mx-grid-icon" style="' +
					getColorStyle(item, column, true) +
					'">' +
					(item[column.field] ? 'check_box' : 'check_box_outline_blank') +
					'</md-icon>'
				);
			case 'selectItemCheckbox':
				return (
					'<md-checkbox class="mx-grid-checkbox" ng-checked="grid.appScope.vm.rows[' +
					itemIndex +
					'].selected" aria-hidden="true" role="none" tabindex="-1"></md-checkbox>'
				);
			case 'quickfilter':
				return (
					'<mx-quick-filter static-mode="true" value="' +
					item[column.field] +
					'" aria-hidden="true" role="none" titles="' +
					JSON.stringify(colDefItem.Labels).replace(/"/g, '&#34;') +
					'"  parts="' +
					colDefItem.Parts +
					'"></mx-quick-filter>'
				);
			default:
				return defaultCellTemplate;
		}
	}

	function loadTemplates() {
		return {
			headerTemplate: $templateCache.get('mx-grid/mx-grid-header-cell.html'),
			columnFilter: $templateCache.get('mx-grid/mx-grid-column-filters/mx-grid-column-filter.html'),

			quickFilterHeader: function (labels, parts) {
				return (
					' <mx-quick-filter aria-hidden="true" role="none" value="0" titles="' +
					JSON.stringify(labels).replace(/"/g, '&#34;') +
					'"  parts="' +
					parts +
					'"></mx-quick-filter>'
				);
			},

			selectItemCheckboxHeader: function () {
				return '<md-checkbox class="mx-grid-checkbox" ng-checked="!!grid.appScope.vm.rows.length && grid.appScope.vm.allSelected" aria-hidden="true" role="none" tabindex="-1" ng-click="grid.appScope.vm.selectAllItems()"></md-checkbox>';
			},
		};
	}

	function getStaticCellTemplate(item, column, itemIndex) {
		return (
			'<div style="' +
			getColorStyle(item, column, false) +
			'"' +
			'  class="ui-grid-cell ui-grid-col' +
			column.uid +
			' ' +
			(column.colDef.cellClass ? column.colDef.cellClass : '') +
			' " ' +
			'  role="' +
			(column.colDef.ariaHidden ? 'presentation' : 'gridcell') +
			'"' +
			'  >' +
			getCellContentTemplate(item, column, itemIndex) +
			'</div>'
		);
	}

	function getStaticRowTemplate(columns, item, itemIndex) {
		return (
			'<div role="row" class="mx-grid-row-cells-wrapper">' +
			columns.reduce(function (accumulator, column) {
				return accumulator + getStaticCellTemplate(item, column, itemIndex);
			}, '') +
			'</div>'
		);
	}

	function refreshStaticGrid() {
		vm.staticTemplate = getStaticTemplate(vm.gridApi.grid.columns);
	}

	function getStaticTemplate(columns) {
		var template = '';

		if (!Array.isArray(vm.data)) {
			return template;
		}

		vm.rows = vm.data.map(function (item, index) {
			return {
				entity: item,
				selected: false,
				highlighted: false,
				index: index,
			};
		});

		return vm.data.reduce(function (accumulator, item, index) {
			return (
				accumulator +
				'<div class="ui-grid-row ' +
				(index % 2 !== 0 ? 'ui-grid-row__even' : '') +
				'" tabindex="-1" role="none" data-row-index="' +
				index +
				'">' +
				getStaticRowTemplate(columns, item, index) +
				'</div>'
			);
		}, template);
	}

	function getClickedRow(clickedEl) {
		var row = $(clickedEl).closest('.ui-grid-row');
		if (!row) {
			return null;
		}

		var index = parseInt(row.attr('data-row-index'), 10);
		if (isNaN(index)) {
			return null;
		}
		return vm.rows[index];
	}
}

function MxGrid($templateCache) {
	var directive = {
		restrict: 'E',
		scope: {},
		require: {
			mxForm: '?^mxForm',
			mxContainerCtrl: '?^^mxContainer',
		},
		bindToController: {
			options: '<?',
			title: '@',
			loading: '<',
			data: '<',
			columns: '<?',
			onSelectionChanged: '&',
			enableRowSelection: '@',
			onGroupingClick: '&',
			loadData: '<?',
			selectedItems: '=?',
			onColumnVisibilityChanged: '&',
			onSortChanged: '&',
			onColumnPositionChanged: '&',
			onColumnSizeChanged: '&',
			onReorder: '&',
			currentItem: '=?',
			onSelectAllClick: '&',
		},
		controller: MxGridCtrl,
		controllerAs: 'vm',
		template: template,
		getTemplate: getTemplate,
	};

	directive.compile = function (element, attributes) {
		loadUiGridTemplates($templateCache, attributes);
		return {
			pre: function ($scope, element, attr, ctrl) {
				if (typeof directive.link === 'function') {
					directive.link($scope, element, attr, ctrl);
				}
			},
			post: function () {},
		};
	};

	directive.$applyGridHeightAttributes = function (element, attrs, container) {
		var targetContainer = container || 'mx-grid';
		if (attrs['full-height'] === 'true') {
			var offset = Math.floor($(element).offset().top) + 120;
			$(element)
				.find(targetContainer)
				.css('min-height', 'calc(100vh - ' + offset + 'px)');
		} else if (attrs.minHeight) {
			var mH = Number(attrs.minHeight);
			if (mH) {
				$(element)
					.find(targetContainer)
					.css('min-height', mH + 'px');
			}
		}
	};

	return directive;

	function template(element, attr) {
		return directive.getTemplate(element, attr);
	}

	function getTemplate() {
		return (
			'' +
			'<div' +
			'	ng-class="{ ' +
			"		'mx-grid--zibra': vm.options.rowColoringEnabled," +
			"		'mx-grid--filters-enabled': vm.options.enableFiltering," +
			"		'mx-grid--density-high':	vm.options.rowHeight<30," +
			"		'mx-grid--density-normal':	vm.options.rowHeight==30," +
			"		'mx-grid--density-low':	vm.options.rowHeight>30" +
			'	 }" ' +
			'	ui-grid="vm.gridOptions"' +
			'	ui-grid-resize-columns' +
			'	ui-grid-move-columns' +
			'	ui-grid-edit' +
			'	>' +
			'	<mx-dropdown-menu ' +
			'		items="vm.menuItems" ' +
			'		hide-button="true" ' +
			'		expanded="vm.isDropdownOpen" ' +
			'		event="vm.dropdownEvent" ' +
			'		ng-if="vm.options.onRightClick"></mx-dropdown-menu>' +
			'	<div class="mx-grid-no-data" ng-show="!vm.loading && vm.rows.length === 0 && !vm.rowsRendering">' +
			"		{{'components.common.noData' | mxi18n }}" +
			'	</div>' +
			'	<div class="mx-grid-loading mx-loading-bg" ng-show="vm.loading || vm.rowsRendering"></div>' + // please don't add extra conditions here, see comment for data in doc header
			'</div>'
		);
	}
}

function loadUiGridTemplates($templateCache, attributes) {
	attributes = attributes || {};
	if ($templateCache) {
		var originaMenuBtnTemplate = $templateCache.get('ui-grid/origin/ui-grid-menu-button');
		var originaUiGridTemplate = $templateCache.get('ui-grid/origin/ui-grid');
		// TODO: Why don't just use `vm.options` for passing this flag?
		if (attributes.skipheaderactions === undefined) {
			if (!originaMenuBtnTemplate) {
				$templateCache.put('ui-grid/origin/ui-grid-menu-button', $templateCache.get('ui-grid/ui-grid-menu-button'));
				$templateCache.put('ui-grid/origin/ui-grid', $templateCache.get('ui-grid/ui-grid'));
			}
			$templateCache.put('ui-grid/ui-grid-menu-button', $templateCache.get('mx-grid/mx-grid-menu-button.html'));
			$templateCache.put('ui-grid/ui-grid', $templateCache.get('mx-grid/mx-grid.html'));
		} else {
			if (originaMenuBtnTemplate) {
				$templateCache.put('ui-grid/ui-grid-menu-button', originaMenuBtnTemplate);
				$templateCache.put('ui-grid/ui-grid', originaUiGridTemplate);
			}
		}

		// Here we set common `cell-template` for all grids.
		// In a case we need to specify it - it could be done via `cellTemplate` param in columnDefs

		//Override default Angular UI grid Templates by custom ones
		$templateCache.put('ui-grid/uiGridMenu', $templateCache.get('mx-grid/mx-grid-gridmenu.html'));
		$templateCache.put('ui-grid/uiGridViewport', $templateCache.get('mx-grid/mx-grid-viewport.html'));
		$templateCache.put('ui-grid/uiGridMenuItem', $templateCache.get('mx-grid/mx-grid-gridmenu-item.html'));

		$templateCache.put(
			'ui-grid/ui-grid-header',
			$templateCache.get('ui-grid/ui-grid-header').replace('role="columnheader"', "role=\"{{:: col.colDef.ariaHidden ? 'presentation' : 'columnheader' }}\"")
		);
	}
}

window.mx = window.mx || {};
window.mx.components = window.mx.components || {};
window.mx.components.Grid = MxGrid;
window.mx.components.loadUiGridTemplates = loadUiGridTemplates;
angular.module('mx.components').directive('mxGrid', ['$templateCache', MxGrid]);
